import { SQLiteFactory, type WorkerRequestWithResolver } from './SQLiteFactory'

/**
 * Serves as a container for a message and its metadata. Groups transaction messages together.
 *
 * All promise rejections are wrapped such that timeouts will automatically mark a QueueItem as rejected as well
 */
export class QueueItem {
    correlationId?: string
    state: 'pending' | 'committed' | 'rejected'

    messages: WorkerRequestWithResolver[] = []

    static withMessage({
        message,
        correlationId,
    }: {
        message?: WorkerRequestWithResolver
        correlationId?: string
    }) {
        const queueItem = new QueueItem()
        if (correlationId) {
            queueItem.correlationId = correlationId
        }

        if (message) {
            queueItem.pushMessage(message)
        }

        return queueItem
    }

    constructor() {
        this.state = 'pending'
    }

    pushMessage(message: WorkerRequestWithResolver) {
        this.messages.push({
            ...message,
            reject: (reason?: any) => {
                // transaction rejected states are handled in the rollback
                if (!this.correlationId) {
                    this.state = 'rejected'
                    WorkerRequestQueue.getInstance().flush()
                }
                message.reject(reason)
            },
        })
    }
}

/**
 * This class is used to manage the queue of messages to the SQLite worker.
 * It ensures that messages are processed in order and that transactions are completed.
 *
 * If a transaction is currently running:
 *  - all messages belonging to that transaction will be immediately processed.
 *  - the transaction will remain open until it receives a 'transaction_end' message.
 *  - all messages unrelated to transactions will be queued up until the transaction is committed.
 *
 * If a transaction is not currently running, messages will be processed as they come in,
 */
export class WorkerRequestQueue {
    queue: QueueItem[] = []

    static instance: WorkerRequestQueue

    static getInstance() {
        if (!WorkerRequestQueue.instance) {
            WorkerRequestQueue.instance = new WorkerRequestQueue()
        }
        return WorkerRequestQueue.instance
    }

    isTransactionRunning(correlationId: string) {
        return this.queue.length ? this.queue[0].correlationId === correlationId : false
    }

    enqueue(message: WorkerRequestWithResolver) {
        if (message.type === 'start_transaction') {
            this.queue.push(
                QueueItem.withMessage({
                    message,
                    correlationId: message.transactionCorrelationId,
                })
            )
        }
        else if (message.type === 'rollback_transaction' && message.transactionCorrelationId) {
            const queueItem = this.queue.find(
                (qi) => qi.correlationId == message.transactionCorrelationId
            )
            if (!queueItem) {
                console.error('Attempted to rollback a nonexistent transaction')
                this.flush()
                return
            }

            // if tx is running, we know that we've already sent the 'begin' message and can roll back immediately
            if (this.isTransactionRunning(message.transactionCorrelationId)) {
                queueItem.state = 'committed'
                queueItem.messages = [message]
            }
            else {
                queueItem.state = 'rejected'
            }
        }
        else if (message.transactionCorrelationId) {
            const queueItem = this.queue.find(
                (qi) => qi.correlationId == message.transactionCorrelationId
            )
            if (!queueItem) {
                console.error('Received a message for a nonexistent transaction')
                this.flush()
                return
            }

            if (queueItem.state === 'committed') {
                message.reject(JSON.stringify({ error: 'transaction_already_committed' }))
                return
            }

            queueItem.pushMessage(message)

            if (message.type === 'end_transaction') {
                queueItem.state = 'committed'
            }
        }
        else {
            this.queue.push(QueueItem.withMessage({ message }))
        }

        this.flush()
    }

    sendMessage(message: WorkerRequestWithResolver) {
        SQLiteFactory.getInstance().postMessageWithResolver(message)
    }

    // recursively flush all messages from the queue, if possible
    flush() {
        if (!this.queue.length) return

        const first = this.queue[0]
        if (first.state === 'rejected') {
            console.error('Rejected queue item, skipping')
            this.queue.shift()
            this.flush()
            return
        }

        while (first.messages.length) {
            this.sendMessage(first.messages.shift()!)
        }

        // wait for the final 'transaction_end' message
        if (first.correlationId && first.state !== 'committed') {
            // if the transaction is not yet committed, stop processing and keep tx in queue
            return
        }

        this.queue.shift()

        this.flush()
    }
}
